import React, { useState, useEffect, useMemo } from 'react';
import { CenteredModal } from '~/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faShieldAlt, faCheck, faUser, faExclamationTriangle, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import RolePermissionAPI from '~/api/rolePermissionApi';
import permissionRequestApi from '~/api/permissionRequestApi';

function ViewPermissionModal({ show, onClose, user }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [allPermissions, setAllPermissions] = useState([]);
    const [userPermissions, setUserPermissions] = useState([]);
    const [openGroups, setOpenGroups] = useState({});
    const [activeTab, setActiveTab] = useState('permissions');
    const [requestHistory, setRequestHistory] = useState([]);

    const toggleGroup = (group) => {
        setOpenGroups(prev => ({
            ...prev,
            [group]: prev[group] === false ? true : false
        }));
    };

    useEffect(() => {
        if (show && user) {
            setActiveTab('permissions');
            fetchData();
        } else {
            setAllPermissions([]);
            setUserPermissions([]);
            setRequestHistory([]);
            setError('');
        }
    }, [show, user]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');
            const [permsRes, userRes, historyRes] = await Promise.all([
                RolePermissionAPI.getAllPermissions(),
                RolePermissionAPI.getUserPermissions(user.id),
                permissionRequestApi.getUserRequestHistory(user.id).catch(() => null)
            ]);

            if (permsRes.data?.Succeeded) {
                setAllPermissions(permsRes.data.Data || []);
            }
            if (userRes.data?.Succeeded) {
                setUserPermissions(userRes.data.Data || []);
            } else {
                setError(userRes.data?.Message || 'Failed to load user permissions.');
            }

            if (historyRes && (historyRes.data?.Succeeded || historyRes.data?.succeeded)) {
                let historyData = Array.isArray(historyRes.data) ? historyRes.data : (historyRes.data?.data || historyRes.data?.Data || []);
                
                historyData.sort((a, b) => {
                    const timeA = new Date(a.RequestedAt).setMilliseconds(0);
                    const timeB = new Date(b.RequestedAt).setMilliseconds(0);
                    if (timeA !== timeB) return timeB - timeA;
                    return a.RequestedPermission.localeCompare(b.RequestedPermission);
                });

                setRequestHistory(historyData);
            }
        } catch (err) {
            setError(err.response?.data?.Message || 'Failed to load permissions.');
        } finally {
            setLoading(false);
        }
    };

    const groupedPermissions = useMemo(() => {
        const getGroupPath = (rawModule) => {
            const map = {
                'DashboardAnalytics': ['Dashboard', 'Analytics'],
                'DashboardMetrics': ['Dashboard', 'Metrics'],
                'UserRole': ['User Management', 'User Role'],
                'FileManager': ['File Management', 'Image Gallery'],
                'AssetsImage': ['File Management', 'Assets Image'],
                'FileImageUrl': ['File Management', 'Image Url'],
                'PaymentInformation': ['Payment & Subscription', 'Payment Information'],
                'StripePayments': ['Payment & Subscription', 'Stripe Payments'],
                'LandingPageAboutUs': ['Landing Page', 'About Us'],
                'LandingPageOurService': ['Landing Page', 'Our Service'],
                'LandingPagePrivateBlogPost': ['Landing Page', 'Private Blog Post'],
                'LandingPageRoute': ['Landing Page', 'Route'],
                'LandingPageArticle': ['Landing Page', 'Article'],
                'LandingPageTerms': ['Landing Page', 'Terms'],
                'LandingPageHome': ['Landing Page', 'Home'],
                'Appointment': ['System Management', 'General', 'Appointment'],
                'ApplicationParam': ['System Management', 'General', 'Application Parameter'],
                'SupportTicket': ['AI Chatbot Page', 'Support Ticket History'],
                'Notification': ['System Management', 'General', 'Notification'],
                'SystemJob': ['System Management', 'General', 'System Jobs'],
                'DatabaseManagement': ['System Management', 'Database', 'Database Management'],
                'EmailTemplate': ['System Management', 'Email & Messaging', 'Email Template'],
                'SmtpSettings': ['System Management', 'Email & Messaging', 'SMTP Settings'],
                'Mailjet': ['System Management', 'Email & Messaging', 'Mailjet'],
                'NotiInAppTemplate': ['System Management', 'Email & Messaging', 'In-App Notification Template'],
                'AuditLog': ['System Management', 'Logs Management', 'Audit Log'],
                'SystemException': ['System Management', 'Logs Management', 'System Exception'],
                'UserVisitor': ['User Management', 'User Visitor'],
                'UserInformation': ['User Management', 'User Information'],
                'UserPlanSubscription': ['Payment & Subscription', 'User Plan Subscription'],
                'ServiceManager': ['Payment & Subscription', 'Service Manager'],
                'UserCardManagement': ['Payment & Subscription', 'User Card Management'],
                'ChatbotManagement': ['AI Chatbot Page', 'Chatbot Management'],
                'ChatbotFeedback': ['AI Chatbot Page', 'Chat Feedback'],
                'AwsChatbot': ['AI Chatbot Page', 'Manage Chat Sessions'],
                'QdrantSync': ['AI Chatbot Page', 'Qdrant Vector Sync'],
                'AiServicesUsage': ['System Management', 'AI Services', 'Usage'],
                'AiServicesPricing': ['System Management', 'AI Services', 'OpenAI Pricing'],
                'AiServicesMasterData': ['System Management', 'AI Services', 'Master Data'],
                'AiServicesTrafficAnalytics': ['System Management', 'AI Services', 'Traffic Analytics'],
                'ApiMonitoring': ['System Management', 'API Monitoring & Testing'],
                'HeaderDatabaseManagement': ['Header', 'Database Management'],
                'HeaderSystemServicesTesting': ['Header', 'System Services Testing'],
                'HeaderSecuritySettings': ['Header', 'Security Settings'],
                'HeaderSearchSettings': ['Header', 'Search Settings'],
                'HeaderCreateUserAccount': ['Header', 'Create User Account'],
                'SecuritySettings': ['Header', 'Security Settings']
            };
            if (map[rawModule]) return map[rawModule];
            
            if (rawModule.startsWith('LandingPage')) return ['Landing Page', rawModule.replace('LandingPage', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Dashboard')) return ['Dashboard', rawModule.replace('Dashboard', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Aws')) return ['AWS', rawModule.replace('Aws', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Chatbot')) return ['Chatbot', rawModule.replace('Chatbot', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            
            return ['Other', rawModule.replace(/([a-z])([A-Z])/g, '$1 $2')];
        };

        const root = {};
        allPermissions
            .filter(p => userPermissions.includes(p) && !p.includes('Header.CreateAdminAccount') && !p.includes('Header.RoleManagement') && !p.includes('SystemSettings'))
            .forEach(p => {
            const parts = p.split('.');
            let rawModule = parts.length > 1 ? parts[1] : 'Other';
            if (rawModule === 'Header' && parts.length > 2) {
                rawModule = 'Header' + parts[2];
            }
            
            const path = getGroupPath(rawModule);
            const leafNodeName = path[path.length - 1];

            let displayAction = '';
            if (parts.length > 2) {
                let rawAction = parts[2].replace(/([a-z])([A-Z])/g, '$1 $2');
                if (rawModule === 'FileManager' && rawAction === 'Upload') {
                    rawAction = 'Upload & Rename';
                }
                if (rawModule === 'FileManager' && rawAction === 'Delete') {
                    rawAction = 'Delete & Bulk Delete';
                }
                if (rawModule === 'UserPlanSubscription' && rawAction === 'Assign Plan') {
                    rawAction = 'Assign Plan & Assign Specific User';
                }
                if (['LandingPageArticle', 'LandingPageFeatureApp', 'LandingPagePrivateBlogPost'].includes(rawModule) && rawAction === 'Create') {
                    rawAction = 'Add New';
                }
                if (['LandingPageAboutUs', 'LandingPageHome', 'LandingPageTerms'].includes(rawModule) && rawAction === 'Create') {
                    rawAction = 'Add New & Edit';
                }
                if (rawModule === 'Appointment' && rawAction === 'Create') {
                    rawAction = 'Add New';
                }
                if (rawModule === 'SystemJob') {
                    if (rawAction === 'View') rawAction = 'View Detail';
                    if (rawAction === 'Edit') rawAction = 'Enable/Disable';
                }
                if (rawModule === 'SmtpSettings' && rawAction === 'Edit') {
                    rawAction = 'Save Configuration & Test Connection';
                }
                if (rawModule === 'AiServicesMasterData' && rawAction === 'Create') {
                    rawAction = 'Add New & Edit';
                }
                if (rawModule === 'AuditLog' && rawAction === 'Delete') {
                    rawAction = 'Delete (Filter Data & Selected)';
                }
                if (rawModule === 'SystemException' && rawAction === 'Delete') {
                    rawAction = 'Delete Selected';
                }
                if (rawModule === 'ChatbotManagement' && rawAction === 'Create') {
                    rawAction = 'Add New';
                }
                if (rawModule === 'QdrantSync' && rawAction === 'Compare') {
                    rawAction = 'Compare Data';
                }
                displayAction = `${rawAction} ${leafNodeName}`;
            } else {
                displayAction = `Manage ${leafNodeName}`;
            }
            
            let current = root;
            for (let i = 0; i < path.length; i++) {
                const nodeName = path[i];
                if (i === path.length - 1) {
                    if (!current[nodeName]) current[nodeName] = { _isTab: true, perms: [] };
                    current[nodeName].perms.push({ id: p, action: displayAction });
                } else {
                    if (!current[nodeName]) current[nodeName] = {};
                    current = current[nodeName];
                }
            }
        });
        
        return root;
    }, [allPermissions, userPermissions]);

    const renderNode = (nodeName, nodeContent, pathPath, level = 0) => {
        if (nodeContent._isTab) {
            const perms = nodeContent.perms;
            const accessPerm = perms.find(p => p.id.endsWith('.Access'));
            const actionPerms = perms.filter(p => !p.id.endsWith('.Access'));
            
            const allChecked = perms.every(p => userPermissions.includes(p.id));
            const someChecked = perms.some(p => userPermissions.includes(p.id));
            const isIndeterminate = someChecked && !allChecked;
            const isOpen = openGroups[pathPath] !== false;
            
            return (
                <div className="tree-group mb-2" key={pathPath} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                    <div className="d-flex align-items-center" style={{ padding: '4px 0' }}>
                        <div 
                            onClick={() => toggleGroup(pathPath)} 
                            style={{ cursor: 'pointer', width: '20px', display: 'flex', justifyContent: 'center' }}
                        >
                            <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="text-muted" style={{ fontSize: '0.85rem' }} />
                        </div>
                        <div className="m-0 ms-1 d-flex align-items-center">
                            <label className="fw-bold" style={{ fontSize: '0.9rem', color: '#2f5aa2' }}>
                                {nodeName}
                            </label>
                        </div>
                    </div>
                    
                    {isOpen && (
                        <div className="tree-children ms-4 mt-2 mb-3">
                            {accessPerm && (
                                <div className="mb-2 pb-2 d-flex align-items-center" style={{ borderBottom: '1px dashed #e8eaed' }}>
                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#0d6efd', marginRight: '8px' }}></div>
                                    <label className="fs-9 fw-bold text-primary m-0">
                                        Enable Screen Access (Show in Menu{nodeName === 'Image Gallery' ? ', Download, Copy Link' : ''})
                                    </label>
                                </div>
                            )}

                            {actionPerms.length > 0 && (
                                <div className="d-flex flex-column gap-1 ms-2">
                                    <div className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>AVAILABLE ACTIONS</div>
                                    {actionPerms.map(p => (
                                        <div className="m-0 d-flex align-items-center mb-1" key={p.id}>
                                            <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#6c757d', marginRight: '8px', marginLeft: '2px' }}></div>
                                            <label className="fs-9 text-dark m-0">
                                                {p.action}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        } else {
            const isOpen = openGroups[pathPath] !== false; 
            const gatherPerms = (obj) => {
                let pList = [];
                Object.values(obj).forEach(val => {
                    if (val._isTab) pList.push(...val.perms);
                    else pList.push(...gatherPerms(val));
                });
                return pList;
            };
            const allNestedPerms = gatherPerms(nodeContent);
            if (allNestedPerms.length === 0) return null;

            const allChecked = allNestedPerms.every(p => userPermissions.includes(p.id));
            const someChecked = allNestedPerms.some(p => userPermissions.includes(p.id));
            const isIndeterminate = someChecked && !allChecked;

            return (
                <div className="tree-folder mb-3" key={pathPath} style={level === 0 ? { breakInside: 'avoid', pageBreakInside: 'avoid' } : {}}>
                    <div className="d-flex align-items-center" style={{ padding: '6px 0', backgroundColor: level === 0 ? '#fcfcfc' : 'transparent', borderRadius: level === 0 ? '4px' : '0' }}>
                        <div onClick={() => toggleGroup(pathPath)} style={{ cursor: 'pointer', width: '20px', display: 'flex', justifyContent: 'center' }}>
                            <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="text-dark" style={{ fontSize: '0.9rem' }} />
                        </div>
                        <div className="m-0 ms-1 d-flex align-items-center">
                            <label className="fw-bold text-dark" style={{ fontSize: level === 0 ? '1rem' : '0.95rem' }}>
                                {nodeName}
                            </label>
                        </div>
                    </div>
                    
                    {isOpen && (
                        <div className="tree-children ms-4 mt-2">
                            {Object.entries(nodeContent).map(([childName, childContent]) => 
                                renderNode(childName, childContent, `${pathPath}-${childName}`, level + 1)
                            )}
                        </div>
                    )}
                </div>
            );
        }
    };

    const isSuperAdmin = user?.roles?.some(r => r.Name === 'Super Admin');
    const isStandardUser = !isSuperAdmin && user?.roles?.some(r => r.Name === 'User' && user.roles.length === 1);

    return (
        <CenteredModal
            show={show}
            onClose={onClose}
            title={`Permissions: ${user?.name || ''}`}
            size="xl"
            modalType="modal-xl"
            isViewMode={true}
            closeText="Close"
        >
            <div className="pb-0" style={{ minHeight: '400px' }}>
                {error && (
                    <div className="alert alert-danger py-2 fs-9 d-flex align-items-center m-3" role="alert">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        <span>{error}</span>
                    </div>
                )}
                
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="px-3 pb-0">
                        <div className="mb-4">
                            <p className="text-muted mb-3 fw-bold" style={{ fontSize: "0.85rem", fontWeight: 500, lineHeight: "1.5" }}>
                                Review and manage the assigned permissions for this user. You can view their current active permissions across different modules or track the history of their permission requests to monitor access changes over time.
                            </p>
                        </div>
                        <div className="mb-3 border-bottom d-flex gap-4 px-2 nav" role="tablist">
                            <button 
                                className={`btn btn-link text-decoration-none fs-9 fw-bold px-2 pb-2 rounded-0 nav-link custom-tab-btn ${activeTab === 'permissions' ? 'active' : ''}`} 
                                type="button"
                                style={{ marginBottom: '-1px' }}
                                onClick={() => setActiveTab('permissions')}
                            >
                                Current Permissions
                            </button>
                            <button 
                                className={`btn btn-link text-decoration-none fs-9 fw-bold px-2 pb-2 rounded-0 nav-link custom-tab-btn ${activeTab === 'history' ? 'active' : ''}`} 
                                type="button"
                                style={{ marginBottom: '-1px' }}
                                onClick={() => setActiveTab('history')}
                            >
                                Request Access History
                            </button>
                        </div>

                        {activeTab === 'permissions' && (
                            <div style={{ border: '1px solid #d5dbdb', borderRadius: '8px', overflow: 'hidden', marginBottom: '24px' }}>
                                <div style={{ background: '#f2f3f3', borderBottom: '1px solid #d5dbdb', padding: '12px 20px' }}>
                                    <h6 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: '#16191f' }}>Assigned Permissions</h6>
                                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#687078', marginTop: '2px' }}>View the list of active permissions grouped by feature modules.</p>
                                </div>
                                <div style={{ padding: '20px 24px' }}>
                        {isSuperAdmin ? (
                            <div className="text-center" style={{ paddingTop: '60px' }}>
                                <div style={{ 
                                    width: '72px', height: '72px', borderRadius: '50%', 
                                    background: 'linear-gradient(135deg, #fef7e0, #fff3cd)', 
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    marginBottom: '20px'
                                }}>
                                    <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '1.8rem', color: '#f9ab00' }} />
                                </div>
                                <h5 style={{ fontWeight: 700, color: '#202124', marginBottom: '8px' }}>Full System Access</h5>
                                <p style={{ color: '#80868b', fontSize: '0.9rem', maxWidth: '380px', margin: '0 auto' }}>
                                    This user is a Super Admin and possesses all permissions across the system.
                                </p>
                            </div>
                        ) : isStandardUser ? (
                            <div className="text-center" style={{ paddingTop: '60px' }}>
                                <div style={{ 
                                    width: '72px', height: '72px', borderRadius: '50%', 
                                    background: 'linear-gradient(135deg, #e8f0fe, #d2e3fc)', 
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    marginBottom: '20px'
                                }}>
                                    <FontAwesomeIcon icon={faUser} style={{ fontSize: '1.8rem', color: '#1a73e8' }} />
                                </div>
                                <h5 style={{ fontWeight: 700, color: '#202124', marginBottom: '8px' }}>Standard User Access</h5>
                                <p style={{ color: '#80868b', fontSize: '0.9rem', maxWidth: '420px', margin: '0 auto', lineHeight: '1.5' }}>
                                    This user is a standard application user with no granular administrative permissions.
                                </p>
                            </div>
                        ) : (
                            (() => {
                                const rootKeys = Object.keys(groupedPermissions);
                                
                                if (rootKeys.length === 0) {
                                    return (
                                        <div className="text-center" style={{ paddingTop: '60px' }}>
                                            <div style={{ 
                                                width: '72px', height: '72px', borderRadius: '50%', 
                                                background: '#f8f9fa', 
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                marginBottom: '20px', border: '1px dashed #dadce0'
                                            }}>
                                                <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: '1.8rem', color: '#9aa0a6' }} />
                                            </div>
                                            <h5 style={{ fontWeight: 700, color: '#5f6368', marginBottom: '8px' }}>No Permissions Assigned</h5>
                                            <p style={{ color: '#9aa0a6', fontSize: '0.9rem', maxWidth: '420px', margin: '0 auto', lineHeight: '1.5' }}>
                                                This user currently does not have any granular permissions assigned.
                                            </p>
                                        </div>
                                    );
                                }

                                const half = Math.ceil(rootKeys.length / 2);
                                const leftKeys = rootKeys.slice(0, half);
                                const rightKeys = rootKeys.slice(half);

                                return (
                                    <div className="row m-0 tree-view-container">
                                        <div className="col-md-6 border-end pe-4">
                                            {leftKeys.map(k => renderNode(k, groupedPermissions[k], k, 0))}
                                        </div>
                                        <div className="col-md-6 ps-4">
                                            {rightKeys.map(k => renderNode(k, groupedPermissions[k], k, 0))}
                                        </div>
                                    </div>
                                );
                            })()
                        )}
                                </div>
                            </div>
                        )}
                    
                        {activeTab === 'history' && (
                            <div style={{ border: '1px solid #d5dbdb', borderRadius: '8px', overflow: 'hidden', marginBottom: '24px' }}>
                                <div style={{ background: '#f2f3f3', borderBottom: '1px solid #d5dbdb', padding: '12px 20px' }}>
                                    <h6 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: '#16191f' }}>Permission Request Log</h6>
                                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#687078', marginTop: '2px' }}>Track the history of permission requests submitted by this user.</p>
                                </div>
                                <div className="p-0">
                                    {requestHistory.length === 0 ? (
                                        <div className="text-center" style={{ paddingTop: '60px' }}>
                                            <div style={{ 
                                                width: '72px', height: '72px', borderRadius: '50%', 
                                                background: '#f8f9fa', 
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                marginBottom: '20px', border: '1px dashed #dadce0'
                                            }}>
                                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9aa0a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <polyline points="12 6 12 12 16 14"></polyline>
                                                </svg>
                                            </div>
                                            <h5 style={{ fontWeight: 700, color: '#5f6368', marginBottom: '8px' }}>No Request History</h5>
                                            <p style={{ color: '#9aa0a6', fontSize: '0.9rem', maxWidth: '420px', margin: '0 auto', lineHeight: '1.5', paddingBottom: '60px' }}>
                                                This user has not made any permission access requests yet.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive m-0" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                            <table className="table table-hover table-striped mb-0 fs-9">
                                                <thead className="table-light sticky-top">
                                                    <tr>
                                                        <th className="py-2 ps-4" style={{ width: '35%' }}>Requested Permission</th>
                                                        <th className="py-2" style={{ width: '25%' }}>Reason</th>
                                                        <th className="py-2" style={{ width: '15%' }}>Date</th>
                                                        <th className="py-2 text-center" style={{ width: '10%' }}>Status</th>
                                                        <th className="py-2 pe-4" style={{ width: '15%' }}>Reject Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {requestHistory.map(r => {
                                                const isAccess = r.RequestedPermission.endsWith('.Access');
                                                const prefixParts = r.RequestedPermission.split('.');
                                                prefixParts.pop();
                                                const prefix = prefixParts.join('.');
                                                const rTime = new Date(r.RequestedAt).setMilliseconds(0);
                                                const hasAccessSibling = requestHistory.some(req => 
                                                    req.RequestedPermission === `${prefix}.Access` && 
                                                    new Date(req.RequestedAt).setMilliseconds(0) === rTime
                                                );
                                                
                                                const isParent = isAccess || !hasAccessSibling;

                                                return (
                                                <tr key={r.Id} className={`align-middle ${isParent ? 'fw-bold' : ''}`} style={{ backgroundColor: isParent ? '#f8f9fa' : 'transparent' }}>
                                                    <td className="ps-4">
                                                        {!isParent ? (
                                                            <div className="d-flex align-items-center ms-4">
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#aab7b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2 mt-1">
                                                                    <polyline points="9 18 15 12 9 6"></polyline>
                                                                </svg>
                                                                <code>{r.RequestedPermission}</code>
                                                            </div>
                                                        ) : (
                                                            <div className="d-flex align-items-center">
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#687078" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                                                                    <rect x="3" y="3" width="7" height="7"></rect>
                                                                    <rect x="14" y="3" width="7" height="7"></rect>
                                                                    <rect x="14" y="14" width="7" height="7"></rect>
                                                                    <rect x="3" y="14" width="7" height="7"></rect>
                                                                </svg>
                                                                <code className="fw-bold" style={{ fontSize: '13px' }}>{r.RequestedPermission}</code>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td><div className="text-muted text-break" style={{ fontSize: "0.85rem", maxWidth: '200px' }}>{r.Reason}</div></td>
                                                    <td className="text-muted" style={{ fontSize: "0.85rem" }}>{new Date(r.RequestedAt).toLocaleString()}</td>
                                                    <td className="text-center">
                                                        <span className={`badge ${r.Status === 1 ? 'bg-success' : r.Status === 2 ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                            {r.Status === 1 ? 'Approved' : r.Status === 2 ? 'Rejected' : 'Pending'}
                                                        </span>
                                                    </td>
                                                    <td className="pe-4"><div className="text-muted text-break" style={{ fontSize: "0.85rem", maxWidth: '200px' }}>{r.RejectReason || '-'}</div></td>
                                                </tr>
                                                );
                                            })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </CenteredModal>
    );
}

export default ViewPermissionModal;
